import React, { useCallback, useMemo, useReducer } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { AlertCircle } from 'lucide-react';
import { SceneEditorHeader } from './SceneEditorHeader';
import { SceneEditorBody } from './SceneEditorBody';
import { RegenerateModalWrapper, FinalVideoDialog } from './SceneEditorModals';
import { useSceneEditorOrchestration } from '@/hooks/useSceneEditorOrchestration';
import { api } from '../../../convex/_generated/api';
import type { ISceneEditorContainerProps, SceneType } from '@/@types/reelScenes';
import type { FrameMode, SidebarTab } from '@/@types/sceneEditorV2';
import type { VideoThumbnailSelection } from '@/@types/sceneEditorRedesign';
import type { ICaptionSettings } from '@/@types/captions';
import { DEFAULT_CAPTION_SETTINGS } from '@/@types/captions';
import type { Id } from '../../../convex/_generated/dataModel';

function ErrorAlert({ error }: { error: string }) {
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 p-4" role="alert">
      <div className="flex items-start gap-3">
        <AlertCircle className="h-5 w-5 flex-shrink-0 text-red-600" />
        <div>
          <h4 className="font-medium text-red-900">Error</h4>
          <p className="mt-1 text-sm text-red-700">{error}</p>
        </div>
      </div>
    </div>
  );
}

interface IEditorUIState {
  frameMode: FrameMode;
  sidebarTab: SidebarTab;
  selectedThumbnailOverride: VideoThumbnailSelection | null;
  isRegenModalOpen: boolean;
  timelineCurrentTime: number;
  isTimelinePlaying: boolean;
  showFinalVideo: boolean;
}

type EditorUIAction =
  | { type: 'SET_FRAME_MODE'; mode: FrameMode }
  | { type: 'SET_SIDEBAR_TAB'; tab: SidebarTab }
  | { type: 'SET_THUMBNAIL_OVERRIDE'; selection: VideoThumbnailSelection | null }
  | { type: 'SET_REGEN_MODAL'; open: boolean }
  | { type: 'SET_TIMELINE_TIME'; time: number }
  | { type: 'SET_TIMELINE_PLAYING'; playing: boolean }
  | { type: 'SET_SHOW_FINAL_VIDEO'; show: boolean };

function editorUIReducer(state: IEditorUIState, action: EditorUIAction): IEditorUIState {
  switch (action.type) {
    case 'SET_FRAME_MODE':
      return { ...state, frameMode: action.mode };
    case 'SET_SIDEBAR_TAB':
      return { ...state, sidebarTab: action.tab };
    case 'SET_THUMBNAIL_OVERRIDE':
      return { ...state, selectedThumbnailOverride: action.selection };
    case 'SET_REGEN_MODAL':
      return { ...state, isRegenModalOpen: action.open };
    case 'SET_TIMELINE_TIME':
      return { ...state, timelineCurrentTime: action.time };
    case 'SET_TIMELINE_PLAYING':
      return { ...state, isTimelinePlaying: action.playing };
    case 'SET_SHOW_FINAL_VIDEO':
      return { ...state, showFinalVideo: action.show };
  }
}

const INITIAL_EDITOR_UI: IEditorUIState = {
  frameMode: 'start-only',
  sidebarTab: 'avatar-voice',
  selectedThumbnailOverride: null,
  isRegenModalOpen: false,
  timelineCurrentTime: 0,
  isTimelinePlaying: false,
  showFinalVideo: false,
};

function SceneEditorContainer({
  reelId,
  sceneSplitStatus,
  reelTitle,
  onBack,
}: ISceneEditorContainerProps) {
  const [ui, dispatchUI] = useReducer(editorUIReducer, INITIAL_EDITOR_UI);

  const setFrameMode: React.Dispatch<React.SetStateAction<FrameMode>> = value => {
    const mode = typeof value === 'function' ? value(ui.frameMode) : value;
    dispatchUI({ type: 'SET_FRAME_MODE', mode });
  };
  const setIsTimelinePlaying: React.Dispatch<React.SetStateAction<boolean>> = value => {
    const playing = typeof value === 'function' ? value(ui.isTimelinePlaying) : value;
    dispatchUI({ type: 'SET_TIMELINE_PLAYING', playing });
  };
  const setTimelineCurrentTime: React.Dispatch<React.SetStateAction<number>> = value => {
    const time = typeof value === 'function' ? value(ui.timelineCurrentTime) : value;
    dispatchUI({ type: 'SET_TIMELINE_TIME', time });
  };
  const setShowFinalVideo: React.Dispatch<React.SetStateAction<boolean>> = value => {
    const show = typeof value === 'function' ? value(ui.showFinalVideo) : value;
    dispatchUI({ type: 'SET_SHOW_FINAL_VIDEO', show });
  };

  const reel = useQuery(api.reels.getReelById, { reelId });
  const updateReelContent = useMutation(api.reels.updateReelContent);
  const saveDefaultCaptionSettings = useMutation(
    api.property.userPreferences.setDefaultCaptionSettings
  );

  const captionSettings: ICaptionSettings = reel?.captionSettings ?? DEFAULT_CAPTION_SETTINGS;

  const handleUpdateCaptionSettings = useCallback(
    (partial: Partial<ICaptionSettings>) => {
      const merged = { ...captionSettings, ...partial };
      void updateReelContent({ reelId, captionSettings: merged });
      void saveDefaultCaptionSettings({ captionSettings: merged });
    },
    [reelId, captionSettings, updateReelContent, saveDefaultCaptionSettings]
  );

  const o = useSceneEditorOrchestration({
    reelId,
    sceneSplitStatus,
    frameMode: ui.frameMode,
    setFrameMode,
    setIsTimelinePlaying,
    setTimelineCurrentTime,
    setShowFinalVideo,
    selectedThumbnailOverride: ui.selectedThumbnailOverride,
  });

  const { selectedScene, saveScene, libraryAvatars } = o;
  const handleLayoutChange = useCallback(
    (sceneType: SceneType) => {
      if (!selectedScene) return;
      const needsAvatar =
        sceneType === 'avatar_only' ||
        sceneType === 'avatar_broll_split' ||
        sceneType === 'avatar_image_split';
      const hasAvatar = !!selectedScene.libraryImageId;

      if (needsAvatar && !hasAvatar && libraryAvatars.length > 0) {
        const defaultAvatar = libraryAvatars.find(a => a.isDefault) ?? libraryAvatars[0];
        void saveScene(selectedScene._id, {
          editedSceneType: sceneType,
          libraryImageId: defaultAvatar._id,
        });
      } else {
        void saveScene(selectedScene._id, { editedSceneType: sceneType });
      }
    },
    [selectedScene, saveScene, libraryAvatars]
  );

  const scriptPanelProps = useMemo(
    () => ({
      onPlayAudio: o.handlePlayAudio,
      onDeleteScene: o.handleDeleteScene,
      onRegenerateAudio: o.handleRegenerateAudio,
      onAddScene: o.handleAddScene,
      onUpdateVoiceoverText: o.handleUpdateVoiceoverText,
      onUpdateDeliveryStyle: (() => {}) as (sceneId: Id<'reelScenes'>, prompt: string) => void,
      audioGeneratingSceneIds: o.voiceoverGeneratingSceneIds,
      audioPlayingSceneId: o.audioPlayingSceneId,
      voiceoversMap: o.scriptPanelVoiceoversMap,
    }),
    [
      o.handlePlayAudio,
      o.handleDeleteScene,
      o.handleRegenerateAudio,
      o.handleAddScene,
      o.handleUpdateVoiceoverText,
      o.voiceoverGeneratingSceneIds,
      o.audioPlayingSceneId,
      o.scriptPanelVoiceoversMap,
    ]
  );

  const sidebarProps = useMemo(
    () => ({
      activeTab: ui.sidebarTab,
      onTabChange: (tab: SidebarTab) => dispatchUI({ type: 'SET_SIDEBAR_TAB', tab }),
      avatarName: o.avatarName,
      avatarImageUrl: o.avatarImageUrl,
      voiceName: o.voiceName,
      onSelectAvatar: o.handleSelectAvatar,
      onSelectVoice: o.setDefaultVoice,
      onRenderScene: o.handleRenderScene,
      isRendering: o.isRendering || o.isGeneratingSelectedSceneVideo,
      canRender: o.canRenderSelectedScene,
      persons: o.persons,
      voices: o.activeVoices,
      libraryAvatars: o.libraryAvatars,
      selectedLibraryImageId: o.selectedScene?.libraryImageId ?? undefined,
      selectedSceneType: o.effectiveValues?.sceneType ?? 'video_only',
      onLayoutChange: handleLayoutChange,
      captionSettings,
      onUpdateCaptionSettings: handleUpdateCaptionSettings,
    }),
    [
      ui.sidebarTab,
      o.avatarName,
      o.avatarImageUrl,
      o.voiceName,
      o.handleSelectAvatar,
      o.setDefaultVoice,
      o.handleRenderScene,
      o.isRendering,
      o.isGeneratingSelectedSceneVideo,
      o.canRenderSelectedScene,
      o.persons,
      o.activeVoices,
      o.libraryAvatars,
      o.selectedScene?.libraryImageId,
      o.effectiveValues?.sceneType,
      handleLayoutChange,
      captionSettings,
      handleUpdateCaptionSettings,
    ]
  );

  return (
    <div className="flex h-screen flex-col bg-background">
      <SceneEditorHeader
        reelTitle={reelTitle}
        sceneCount={o.splittingState.scenes.length}
        totalDuration={o.totalDuration}
        onBack={onBack}
        frameMode={ui.frameMode}
        onToggleFrameMode={o.handleToggleFrameMode}
        onOpenRegenerateMedia={() => dispatchUI({ type: 'SET_REGEN_MODAL', open: true })}
        onPlayPreview={o.handleTimelinePlayPause}
        onGenerate={o.generateFullReel}
        isGenerating={o.isGeneratingFull || o.isGeneratingImages || o.isRendering}
        generateLabel={o.effectiveGenerateLabel}
        generateProgress={o.generateProgress}
        selectedSceneType={o.effectiveValues?.sceneType ?? null}
        onRegenerateVideo={o.handleRenderScene}
        onViewFinalVideo={() => dispatchUI({ type: 'SET_SHOW_FINAL_VIDEO', show: true })}
      />

      {(o.splittingState.error || o.editorState.error || o.noScriptError) && (
        <div className="space-y-2 px-4 pt-3">
          {o.splittingState.error && <ErrorAlert error={o.splittingState.error} />}
          {o.editorState.error && <ErrorAlert error={o.editorState.error} />}
          {o.noScriptError && (
            <ErrorAlert error="No script found. Go back and add a script first." />
          )}
        </div>
      )}

      <SceneEditorBody
        isSplitting={o.splittingState.isSplitting}
        isAutoSplitting={o.isAutoSplitting}
        sortedScenes={o.sortedScenes}
        selectedIndex={o.selectedIndex}
        selectedScene={o.selectedScene}
        selectScene={o.selectScene}
        frameMode={ui.frameMode}
        firstFrameUrl={o.firstFrameUrl}
        lastFrameUrl={o.lastFrameUrl}
        videoUrl={o.videoUrl}
        avatarImageUrl={o.avatarImageUrl}
        selectedThumbnail={o.selectedThumbnail}
        onSelectThumbnail={(selection: VideoThumbnailSelection | null) =>
          dispatchUI({ type: 'SET_THUMBNAIL_OVERRIDE', selection })
        }
        isGeneratingSelectedSceneImages={o.isGeneratingSelectedSceneImages}
        isGeneratingSelectedSceneVideo={o.isGeneratingSelectedSceneVideo}
        isBurningCaptions={o.isBurningCaptions}
        isRendering={o.isRendering}
        totalDuration={o.totalDuration}
        timelineCurrentTime={ui.timelineCurrentTime}
        isTimelinePlaying={ui.isTimelinePlaying}
        onTimelinePlayPause={o.handleTimelinePlayPause}
        onTimelineSeek={o.handleTimelineSeek}
        sceneThumbnailUrls={o.sceneThumbnailUrls}
        scriptPanelProps={scriptPanelProps}
        sidebarProps={sidebarProps}
      />

      <RegenerateModalWrapper
        selectedScene={o.selectedScene}
        effectiveImagePrompt={o.effectiveValues?.imagePrompt ?? null}
        isRegenModalOpen={ui.isRegenModalOpen}
        onCloseRegenModal={() => dispatchUI({ type: 'SET_REGEN_MODAL', open: false })}
        firstFrameUrl={o.firstFrameUrl}
        lastFrameUrl={o.lastFrameUrl}
        videoUrl={o.videoUrl}
        singleImageSceneId={o.singleImageState.sceneId}
        singleImageFrame={o.singleImageState.frame}
        onRegenerateImage={o.handleRegenerateImage}
      />

      <FinalVideoDialog
        showFinalVideo={ui.showFinalVideo}
        setShowFinalVideo={setShowFinalVideo}
        reelId={reelId}
      />
    </div>
  );
}

export { SceneEditorContainer };
